import { Cpu, Gauge, Grid3x3, Shuffle, TrendingDown, TrendingUp } from 'lucide-react';
import type { TopicDefinition } from '@/shared/surfaces/TopicCatalogTypes';

/**
 * Behavior surface catalog. The behavioral view of the classified interaction
 * data: the kinds and difficulty of work, model fit, and how activity moves
 * over time. Order matters — it is the interior nav order (the Use Cases view
 * is a dedicated feature page ahead of these). Ranked/donut topics drill
 * through a per-person breakdown (`drillField`) before reaching prompt-level
 * evidence.
 */
export const BEHAVIOR_CATALOG: TopicDefinition[] = [
    {
        id: 'difficulty-mix',
        title: 'How hard is the work?',
        navLabel: 'Difficulty',
        hook: 'The difficulty distribution says a lot about trust.',
        question: 'How does activity break down by task difficulty?',
        icon: Gauge,
        accent: 'amber',
        vizKind: 'donut',
        primaryField: 'difficulty',
        drillField: 'user_id',
        drillLabel: 'person',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'difficulty',
        lensPlural: 'difficulty levels',
    },
    {
        id: 'model-fit',
        title: 'Model fit',
        navLabel: 'Model Fit',
        hook: 'Are people reaching for the right model for each kind of work? The fit gauge tracks how often premium models are pointed at low-complexity work.',
        question: 'Is each job going to the right model?',
        icon: Cpu,
        accent: 'indigo',
        vizKind: 'donut',
        primaryField: 'model',
        drillField: 'user_id',
        drillLabel: 'person',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'model',
        lensPlural: 'models',
    },
    {
        id: 'model-by-task',
        title: 'Models by task',
        navLabel: 'Models by Task',
        hook: 'Different models get pulled out for different jobs.',
        question: 'Which models handle which kinds of work?',
        icon: Grid3x3,
        accent: 'cyan',
        vizKind: 'matrix',
        primaryField: 'model',
        secondaryField: 'task_category',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'model',
        lensPlural: 'models',
        matrixRowLimit: 8,
        matrixOrientation: 'primary-cols',
    },
    {
        id: 'activity-trend',
        title: 'Activity over time',
        navLabel: 'Trend',
        hook: 'Daily rhythm reveals when AI is part of the workflow.',
        question: 'How does daily activity trend?',
        icon: TrendingUp,
        accent: 'violet',
        vizKind: 'trend',
        primaryField: 'task_category',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'task category',
        lensPlural: 'task categories',
        timeBucket: 'day',
    },
    {
        id: 'session-drift',
        title: 'Session Drift',
        navLabel: 'Session Drift',
        hook: 'Threads that wander across unrelated topics burn redundant context tokens on every pivot.',
        question: 'Which sessions drift across unrelated topics?',
        icon: Shuffle,
        accent: 'teal',
        vizKind: 'alerts',
        anomalyKind: 'session_drift',
        primaryField: 'session_id',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'drifted session',
        lensPlural: 'drifted sessions',
    },
    {
        id: 'frustration-signals',
        title: 'Frustration Signals',
        navLabel: 'Frustration',
        hook: 'Abrupt endings followed by near-identical re-prompts are a proxy for failed or unsatisfying responses.',
        question: 'Where are people hitting friction and re-prompting?',
        icon: TrendingDown,
        accent: 'rose',
        vizKind: 'alerts',
        anomalyKind: 're_prompt',
        primaryField: 'user_id',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'person',
        lensPlural: 'people',
    },
];
